import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/components/ui/dialog";

function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [activity, setActivity] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [leadOverride, setLeadOverride] = useState("");
  const [processing, setProcessing] = useState(false);
  
  // Force Subscription states
  const [showForceSubscription, setShowForceSubscription] = useState(false);
  const [forcePlan, setForcePlan] = useState("");
  const [forceIndefinite, setForceIndefinite] = useState(false);
  const [forceReason, setForceReason] = useState("");
  const [forceNotify, setForceNotify] = useState(true);
  
  // Discount Code states
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loadingDiscounts, setLoadingDiscounts] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showCreateDiscount, setShowCreateDiscount] = useState(false);
  const [newDiscount, setNewDiscount] = useState({
    code: "",
    discount_type: "percent",
    discount_value: 20,
    duration: "forever",
    duration_months: 3,
    max_redemptions: null,
    description: ""
  });
  const [showApplyDiscountModal, setShowApplyDiscountModal] = useState(false);
  const [applyDiscountCode, setApplyDiscountCode] = useState("");
  
  // Stripe Test Mode
  const [stripeMode, setStripeMode] = useState({ test_mode: false, mode: 'live', api_key_prefix: null });
  const [loadingStripeMode, setLoadingStripeMode] = useState(false);
  
  // Email Discovery (Hunter.io) - Admin Managed
  const [hunterStatus, setHunterStatus] = useState({ configured: false });
  const [loadingHunter, setLoadingHunter] = useState(false);
  const [newHunterKey, setNewHunterKey] = useState("");
  const [savingHunterKey, setSavingHunterKey] = useState(false);

  // AudienceLab - Admin Managed (DEPRECATED)
  const [audienceLabStatus, setAudienceLabStatus] = useState({ configured: false });
  const [loadingAudienceLab, setLoadingAudienceLab] = useState(false);
  const [newAudienceLabKey, setNewAudienceLabKey] = useState("");
  const [savingAudienceLabKey, setSavingAudienceLabKey] = useState(false);

  // Apollo.io - Admin Managed
  const [apolloStatus, setApolloStatus] = useState({ configured: false });
  const [loadingApollo, setLoadingApollo] = useState(false);
  const [newApolloKey, setNewApolloKey] = useState("");
  const [savingApolloKey, setSavingApolloKey] = useState(false);

  // Admin Logs states
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsStats, setLogsStats] = useState({});
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logFilter, setLogFilter] = useState("all");

  // Available subscription plans
  const subscriptionPlans = [
    { id: "free", name: "Free", price: "$0", leads: 10 },
    { id: "basic", name: "Basic", price: "$97", leads: 0 },
    { id: "starter", name: "Starter", price: "$199", leads: 100 },
    { id: "growth", name: "Growth", price: "$399", leads: 250 },
    { id: "scale", name: "Scale", price: "$699", leads: 500 }
  ];

  useEffect(() => {
    loadUsers();
    loadStripeMode();
    loadHunterStatus();
    loadApolloStatus();
    loadDiscountCodes();
  }, []);

  const loadHunterStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/hunter/status`);
      setHunterStatus(response.data);
    } catch (error) {
      console.error("Error loading Hunter status:", error);
    }
  };

  const loadAudienceLabStatus = async () => {
    setLoadingAudienceLab(true);
    try {
      const response = await axios.get(`${API}/admin/audiencelab/status`);
      setAudienceLabStatus(response.data);
    } catch (error) {
      console.error("Error loading AudienceLab status:", error);
    } finally {
      setLoadingAudienceLab(false);
    }
  };

  const loadApolloStatus = async () => {
    setLoadingApollo(true);
    try {
      const response = await axios.get(`${API}/admin/apollo/status`);
      setApolloStatus(response.data);
    } catch (error) {
      console.error("Error loading Apollo status:", error);
    } finally {
      setLoadingApollo(false);
    }
  };

  const handleSaveHunterKey = async () => {
    if (!newHunterKey.trim()) {
      toast.error("Please enter an API key");
      return;
    }
    
    setSavingHunterKey(true);
    try {
      const response = await axios.post(`${API}/admin/hunter/key?api_key=${encodeURIComponent(newHunterKey.trim())}`);
      toast.success(response.data.message);
      setNewHunterKey("");
      await loadHunterStatus();
    } catch (error) {
      console.error("Error saving Hunter key:", error);
      toast.error(error.response?.data?.detail || "Failed to save API key");
    } finally {
      setSavingHunterKey(false);
    }
  };

  const handleSaveAudienceLabKey = async () => {
    if (!newAudienceLabKey.trim()) {
      toast.error("Please enter an API key");
      return;
    }
    
    // Validate key format
    if (!newAudienceLabKey.trim().startsWith('sk_')) {
      toast.error("Invalid key format. AudienceLab API keys start with 'sk_'");
      return;
    }
    
    setSavingAudienceLabKey(true);
    try {
      const response = await axios.post(`${API}/admin/audiencelab/key?api_key=${encodeURIComponent(newAudienceLabKey.trim())}`);
      if (response.data?.success) {
        toast.success(response.data.message || "AudienceLab API key saved successfully");
      } else {
        toast.success("AudienceLab API key saved");
      }
      setNewAudienceLabKey("");
      await loadAudienceLabStatus();
    } catch (error) {
      console.error("Error saving AudienceLab key:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to save API key";
      toast.error(errorMsg);
    } finally {
      setSavingAudienceLabKey(false);
    }
  };

  const handleSaveApolloKey = async () => {
    if (!newApolloKey.trim()) {
      toast.error("Please enter an API key");
      return;
    }
    
    setSavingApolloKey(true);
    try {
      const response = await axios.post(`${API}/admin/apollo/key?api_key=${encodeURIComponent(newApolloKey.trim())}`);
      if (response.data?.success) {
        toast.success(response.data.message || "Apollo.io API key saved successfully");
      } else {
        toast.success("Apollo.io API key saved");
      }
      setNewApolloKey("");
      await loadApolloStatus();
    } catch (error) {
      console.error("Error saving Apollo key:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to save API key";
      toast.error(errorMsg);
    } finally {
      setSavingApolloKey(false);
    }
  };

  const loadStripeMode = async () => {
    try {
      const response = await axios.get(`${API}/admin/stripe/mode`);
      setStripeMode(response.data);
    } catch (error) {
      console.error("Error loading Stripe mode:", error);
    }
  };

  const toggleStripeMode = async () => {
    setLoadingStripeMode(true);
    try {
      const newTestMode = !stripeMode.test_mode;
      const response = await axios.post(`${API}/admin/stripe/mode?test_mode=${newTestMode}`);
      setStripeMode(response.data);
      toast.success(response.data.message);
    } catch (error) {
      console.error("Error toggling Stripe mode:", error);
      toast.error(error.response?.data?.detail || "Failed to toggle Stripe mode");
    } finally {
      setLoadingStripeMode(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data.users);
    } catch (error) {
      console.error("Error loading users:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error("Failed to load users");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setProcessing(true);
    try {
      await axios.post(`${API}/admin/users/${selectedUser.username}/reset-password`, {
        new_password: newPassword
      });
      toast.success(`Password reset for ${selectedUser.username}. User must change on next login.`);
      setShowResetModal(false);
      setNewPassword("");
      setSelectedUser(null);
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleStatus = async (username, currentStatus) => {
    const action = currentStatus ? "enable" : "disable";
    if (!window.confirm(`Are you sure you want to ${action} ${username}?`)) {
      return;
    }

    try {
      await axios.put(`${API}/admin/users/${username}/toggle-status`);
      toast.success(`User ${username} has been ${action}d`);
      await loadUsers();
    } catch (error) {
      console.error("Error toggling status:", error);
      toast.error("Failed to update user status");
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`⚠️ Delete ${username} and ALL their data? This cannot be undone!`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${username}`);
      toast.success(`User ${username} and all data deleted`);
      await loadUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleRecoverUser = async (username) => {
    if (!window.confirm(`Recover the account for ${username}?`)) {
      return;
    }

    try {
      await axios.post(`${API}/admin/users/${username}/recover`);
      toast.success(`Account ${username} has been recovered`);
      await loadUsers();
    } catch (error) {
      console.error("Error recovering user:", error);
      toast.error(error.response?.data?.detail || "Failed to recover account");
    }
  };

  const handleSetRole = async (username, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${username}/set-role`, { role: newRole });
      toast.success(`${username} is now ${newRole === 'admin' ? 'an admin' : 'a regular user'}`);
      await loadUsers();
    } catch (error) {
      console.error("Error setting role:", error);
      toast.error("Failed to update user role");
    }
  };

  const handleViewActivity = async (user) => {
    setSelectedUser(user);
    setShowActivityModal(true);
    try {
      const response = await axios.get(`${API}/admin/users/${user.username}/activity`);
      setActivity(response.data);
    } catch (error) {
      console.error("Error loading activity:", error);
      toast.error("Failed to load user activity");
    }
  };

  const handleViewSubscription = async (user) => {
    setSelectedUser(user);
    setShowSubscriptionModal(true);
    setSubscription(null);
    try {
      const response = await axios.get(`${API}/admin/users/${user.username}/subscription`);
      setSubscription(response.data);
      setLeadOverride(response.data.subscription?.admin_lead_override ?? "");
    } catch (error) {
      console.error("Error loading subscription:", error);
      toast.error("Failed to load subscription info");
    }
  };

  const handleSetLeadOverride = async () => {
    setProcessing(true);
    try {
      const value = leadOverride === "" ? null : parseInt(leadOverride, 10);
      await axios.put(`${API}/admin/users/${selectedUser.username}/subscription/lead-override`, {
        lead_count: value
      });
      toast.success(value === null 
        ? `Lead override removed for ${selectedUser.username}` 
        : `Lead override set to ${value} for ${selectedUser.username}`);
      // Refresh subscription data
      const response = await axios.get(`${API}/admin/users/${selectedUser.username}/subscription`);
      setSubscription(response.data);
    } catch (error) {
      console.error("Error setting lead override:", error);
      toast.error("Failed to set lead override");
    } finally {
      setProcessing(false);
    }
  };

  const handleTogglePopl = async (enabled) => {
    setProcessing(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.username}/subscription/popl`, {
        enabled: enabled
      });
      toast.success(`Unlimited Pic-to-Lead ${enabled ? 'enabled' : 'disabled'} for ${selectedUser.username}`);
      // Refresh subscription data
      const response = await axios.get(`${API}/admin/users/${selectedUser.username}/subscription`);
      setSubscription(response.data);
    } catch (error) {
      console.error("Error toggling Pic-to-Lead:", error);
      toast.error("Failed to update Pic-to-Lead access");
    } finally {
      setProcessing(false);
    }
  };

  const handleForceSubscription = async () => {
    if (!forcePlan) {
      toast.error("Please select a plan");
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.put(`${API}/admin/users/${selectedUser.username}/subscription/force`, {
        plan: forcePlan,
        is_indefinite: forceIndefinite,
        notify_user: forceNotify,
        reason: forceReason || null
      });
      
      toast.success(response.data.message);
      
      // Refresh subscription data
      const subResponse = await axios.get(`${API}/admin/users/${selectedUser.username}/subscription`);
      setSubscription(subResponse.data);
      
      // Reset force subscription form
      setShowForceSubscription(false);
      setForcePlan("");
      setForceIndefinite(false);
      setForceReason("");
      setForceNotify(true);
      
    } catch (error) {
      console.error("Error forcing subscription:", error);
      toast.error(error.response?.data?.detail || "Failed to change subscription");
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Discount Code Functions
  const loadDiscountCodes = async () => {
    setLoadingDiscounts(true);
    try {
      const response = await axios.get(`${API}/admin/discounts`);
      setDiscountCodes(response.data.discount_codes || []);
    } catch (error) {
      console.error("Error loading discount codes:", error);
    } finally {
      setLoadingDiscounts(false);
    }
  };

  const handleCreateDiscount = async () => {
    if (!newDiscount.discount_value || newDiscount.discount_value <= 0) {
      toast.error("Please enter a valid discount value");
      return;
    }

    setProcessing(true);
    try {
      const payload = {
        ...newDiscount,
        max_redemptions: newDiscount.max_redemptions ? parseInt(newDiscount.max_redemptions) : null
      };
      
      const response = await axios.post(`${API}/admin/discounts`, payload);
      toast.success(`Discount code "${response.data.code}" created!`);
      setShowCreateDiscount(false);
      setNewDiscount({
        code: "",
        discount_type: "percent",
        discount_value: 20,
        duration: "forever",
        duration_months: 3,
        max_redemptions: null,
        description: ""
      });
      loadDiscountCodes();
    } catch (error) {
      console.error("Error creating discount:", error);
      toast.error(error.response?.data?.detail || "Failed to create discount code");
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteDiscount = async (code) => {
    if (!window.confirm(`Are you sure you want to deactivate discount code "${code}"?`)) return;

    try {
      await axios.delete(`${API}/admin/discounts/${code}`);
      toast.success(`Discount code "${code}" deactivated`);
      loadDiscountCodes();
    } catch (error) {
      console.error("Error deleting discount:", error);
      toast.error(error.response?.data?.detail || "Failed to delete discount code");
    }
  };

  const handleApplyDiscountToUser = async () => {
    if (!applyDiscountCode || !selectedUser) return;

    setProcessing(true);
    try {
      const response = await axios.post(`${API}/admin/discounts/apply/${selectedUser.username}`, {
        code: applyDiscountCode,
        apply_to_existing: true
      });
      toast.success(response.data.message);
      setShowApplyDiscountModal(false);
      setApplyDiscountCode("");
      
      // Refresh subscription data
      const subResponse = await axios.get(`${API}/admin/users/${selectedUser.username}/subscription`);
      setSubscription(subResponse.data);
    } catch (error) {
      console.error("Error applying discount:", error);
      toast.error(error.response?.data?.detail || "Failed to apply discount");
    } finally {
      setProcessing(false);
    }
  };

  // Admin Logs functions
  const loadLogs = async (filter = "all") => {
    setLoadingLogs(true);
    try {
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/logs?log_type=${filter}&limit=100`),
        axios.get(`${API}/admin/logs/stats`)
      ]);
      setLogs(logsRes.data.logs || []);
      setLogsStats(statsRes.data || {});
    } catch (error) {
      console.error("Error loading logs:", error);
      toast.error("Failed to load logs");
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleOpenLogs = () => {
    setShowLogsModal(true);
    loadLogs(logFilter);
  };

  const handleLogFilterChange = (filter) => {
    setLogFilter(filter);
    loadLogs(filter);
  };

  const getLogIcon = (logType) => {
    switch (logType) {
      case "oauth": return "🔐";
      case "webhook": return "📥";
      case "contact": return "👤";
      case "audit": return "📋";
      case "error": return "⚠️";
      default: return "📄";
    }
  };

  const getLogColor = (logType) => {
    switch (logType) {
      case "oauth": return "#7c3aed";
      case "webhook": return "#16a34a";
      case "contact": return "#0ea5e9";
      case "audit": return "#f59e0b";
      case "error": return "#dc2626";
      default: return "#64748b";
    }
  };

  const formatLogTime = (log) => {
    const timestamp = log.created_at || log.received_at || log.timestamp || log.forced_at;
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard-page">
      <div className="page-header">
        <h1 className="page-title">🔐 Admin Dashboard</h1>
        <p className="page-description">
          Manage users, reset passwords, and monitor activity
        </p>
      </div>

      {/* Stripe Test Mode Toggle */}
      <div className="content-card" style={{ marginBottom: '24px' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 className="card-title" style={{ marginBottom: '4px' }}>Stripe Payment Mode</h2>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
              Toggle between test and live payment processing
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ textAlign: 'right' }}>
              <span style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: '600',
                background: stripeMode.test_mode ? '#fef3c7' : '#dcfce7',
                color: stripeMode.test_mode ? '#d97706' : '#16a34a'
              }}>
                {stripeMode.test_mode ? '🧪 TEST MODE' : '🔴 LIVE MODE'}
              </span>
              {stripeMode.api_key_prefix && (
                <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                  Key: {stripeMode.api_key_prefix}
                </p>
              )}
            </div>
            <Button
              onClick={toggleStripeMode}
              disabled={loadingStripeMode}
              style={{
                background: stripeMode.test_mode ? '#16a34a' : '#d97706',
                color: 'white',
                minWidth: '140px'
              }}
              data-testid="toggle-stripe-mode-btn"
            >
              {loadingStripeMode ? 'Switching...' : (stripeMode.test_mode ? 'Go LIVE' : 'Enable Test Mode')}
            </Button>
          </div>
        </div>
        {stripeMode.test_mode && (
          <div style={{
            background: '#fffbeb',
            border: '1px solid #fef3c7',
            borderRadius: '8px',
            padding: '12px 16px',
            marginTop: '12px',
            fontSize: '13px',
            color: '#92400e'
          }}>
            <strong>Test Mode Active:</strong> Use card <code style={{ background: '#fef3c7', padding: '2px 6px', borderRadius: '4px' }}>4242 4242 4242 4242</code> with any future date and CVC for testing. No real charges will be made.
          </div>
        )}
        {!stripeMode.test_mode && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px 16px',
            marginTop: '12px',
            fontSize: '13px',
            color: '#991b1b'
          }}>
            <strong>⚠️ Live Mode Active:</strong> Real payments will be processed. Customers will be charged actual money.
          </div>
        )}
      </div>

      {/* Hunter.io API Key Management - Admin Only */}
      <div className="content-card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <div>
            <h2 className="card-title" style={{ marginBottom: '4px' }}>Hunter.io API Key</h2>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
              Global API key for email discovery - used by all users
            </p>
          </div>
        </div>
        
        {/* Current Status */}
        <div style={{ 
          background: hunterStatus.configured ? '#f0fdf4' : '#fef2f2', 
          border: `1px solid ${hunterStatus.configured ? '#bbf7d0' : '#fecaca'}`,
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: hunterStatus.configured ? '#16a34a' : '#dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '16px'
            }}>
              {hunterStatus.configured ? '✓' : '!'}
            </div>
            <div style={{ flex: 1 }}>
              <h4 style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: hunterStatus.configured ? '#16a34a' : '#dc2626',
                marginBottom: '4px'
              }}>
                {hunterStatus.configured ? 'Hunter.io Configured' : 'Hunter.io Not Configured'}
              </h4>
              <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                {hunterStatus.message}
              </p>
            </div>
          </div>
          
          {hunterStatus.configured && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
              gap: '12px',
              marginTop: '16px',
              padding: '12px',
              background: 'white',
              borderRadius: '8px'
            }}>
              <div>
                <span style={{ fontSize: '11px', color: '#64748b' }}>API Key</span>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#334155', margin: '2px 0 0' }}>
                  {hunterStatus.key_prefix}
                </p>
              </div>
              {hunterStatus.searches_available !== null && (
                <>
                  <div>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>Searches Available</span>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#16a34a', margin: '2px 0 0' }}>
                      {hunterStatus.searches_available?.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>Searches Used</span>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#334155', margin: '2px 0 0' }}>
                      {hunterStatus.searches_used?.toLocaleString() || 0}
                    </p>
                  </div>
                  {hunterStatus.plan_name && (
                    <div>
                      <span style={{ fontSize: '11px', color: '#64748b' }}>Plan</span>
                      <p style={{ fontSize: '13px', fontWeight: '600', color: '#334155', margin: '2px 0 0' }}>
                        {hunterStatus.plan_name}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        
        {/* Update API Key */}
        <div>
          <label style={{ fontSize: '14px', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '8px' }}>
            {hunterStatus.configured ? 'Update Hunter.io API Key' : 'Set Hunter.io API Key'}
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <input
              type="password"
              className="form-input"
              placeholder="Enter Hunter.io API key..."
              value={newHunterKey}
              onChange={(e) => setNewHunterKey(e.target.value)}
              style={{ flex: 1, maxWidth: '400px' }}
              data-testid="hunter-api-key-input"
            />
            <Button
              onClick={handleSaveHunterKey}
              disabled={savingHunterKey || !newHunterKey.trim()}
              className="btn btn-primary"
              data-testid="save-hunter-key-btn"
            >
              {savingHunterKey ? 'Saving...' : 'Save Key'}
            </Button>
          </div>
          <p style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
            Get your API key from <a href="https://hunter.io/api-keys" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>hunter.io/api-keys</a>. This key is used globally for all users.
          </p>
        </div>
      </div>

      {/* AudienceLab API Key Management - DEPRECATED */}
      {/* Hidden - Replaced by Apollo.io */}

      {/* Apollo.io API Key Management */}
      <div className="content-card" data-testid="apollo-section">
        <div className="card-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div>
              <h2 className="card-title" style={{ color: '#7c3aed' }}>Apollo.io (Advanced Search)</h2>
              <p style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                Manage the global Apollo.io API key for Advanced Search feature
              </p>
            </div>
            <span style={{
              padding: '4px 12px',
              borderRadius: '16px',
              fontSize: '11px',
              fontWeight: '700',
              background: apolloStatus.configured 
                ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' 
                : '#fee2e2',
              color: apolloStatus.configured ? 'white' : '#dc2626'
            }}>
              {apolloStatus.configured ? 'CONFIGURED' : 'NOT CONFIGURED'}
            </span>
          </div>
        </div>

        {/* Current Status */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ fontSize: '14px', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '12px' }}>
            Current Status
          </label>
          {loadingApollo ? (
            <p style={{ fontSize: '13px', color: '#64748b' }}>Loading status...</p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
              {apolloStatus.configured ? (
                <>
                  <div>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>API Key</span>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#334155', margin: '2px 0 0', fontFamily: 'monospace' }}>
                      {apolloStatus.key_prefix}
                    </p>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>Status</span>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: apolloStatus.message?.includes('valid') || apolloStatus.message?.includes('working') ? '#16a34a' : '#dc2626', margin: '2px 0 0' }}>
                      {apolloStatus.message}
                    </p>
                  </div>
                  {apolloStatus.plan && (
                    <div>
                      <span style={{ fontSize: '11px', color: '#64748b' }}>Service</span>
                      <p style={{ fontSize: '13px', fontWeight: '600', color: '#7c3aed', margin: '2px 0 0' }}>
                        {apolloStatus.plan}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <p style={{ fontSize: '13px', color: '#dc2626' }}>
                  No API key configured. Advanced Search will not be available.
                </p>
              )}
            </div>
          )}
        </div>
        
        {/* Update API Key */}
        <div>
          <label style={{ fontSize: '14px', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '8px' }}>
            {apolloStatus.configured ? 'Update Apollo.io API Key' : 'Set Apollo.io API Key'}
          </label>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <input
              type="password"
              className="form-input"
              placeholder="Enter Apollo.io API key..."
              value={newApolloKey}
              onChange={(e) => setNewApolloKey(e.target.value)}
              style={{ flex: 1, minWidth: '280px', maxWidth: '400px' }}
              data-testid="apollo-api-key-input"
            />
            <Button
              onClick={handleSaveApolloKey}
              disabled={savingApolloKey || !newApolloKey.trim()}
              className="btn btn-primary"
              style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' }}
              data-testid="save-apollo-key-btn"
            >
              {savingApolloKey ? 'Saving...' : 'Save Key'}
            </Button>
          </div>
          <p style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
            Get your API key from <a href="https://app.apollo.io/settings/integrations/api" target="_blank" rel="noopener noreferrer" style={{ color: '#7c3aed' }}>Apollo.io Settings → API</a>. 
            This key is used for Advanced Search feature (admin-only).
          </p>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <Button
          onClick={() => { setShowDiscountModal(true); loadDiscountCodes(); }}
          className="btn"
          style={{ 
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            color: 'white',
            padding: '12px 20px',
            fontSize: '14px',
            fontWeight: '600',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          data-testid="manage-discounts-btn"
        >
          💰 Manage Discount Codes
          {discountCodes.filter(d => d.is_active).length > 0 && (
            <span style={{
              background: 'rgba(255,255,255,0.3)',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '12px'
            }}>
              {discountCodes.filter(d => d.is_active).length} active
            </span>
          )}
        </Button>

        <Button
          onClick={handleOpenLogs}
          className="btn"
          style={{ 
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            color: 'white',
            padding: '12px 20px',
            fontSize: '14px',
            fontWeight: '600',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          data-testid="view-logs-btn"
        >
          📊 View System Logs
        </Button>
      </div>

      {/* System Logs Modal */}
      <Dialog open={showLogsModal} onOpenChange={setShowLogsModal}>
        <DialogContent style={{ maxWidth: "900px", maxHeight: "85vh", overflowY: "auto" }}>
          <DialogHeader>
            <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              📊 System Logs
            </DialogTitle>
          </DialogHeader>
          
          <div style={{ padding: "10px 0" }}>
            {/* Stats Summary */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', 
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{ background: '#f3e8ff', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#7c3aed' }}>{logsStats.oauth_connections || 0}</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>OAuth</div>
              </div>
              <div style={{ background: '#d1fae5', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#16a34a' }}>{logsStats.marketplace_installs || 0}</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>Installs</div>
              </div>
              <div style={{ background: '#e0f2fe', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#0ea5e9' }}>{logsStats.contact_events || 0}</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>Contacts</div>
              </div>
              <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#f59e0b' }}>{logsStats.audit_logs || 0}</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>Audit</div>
              </div>
              <div style={{ background: '#fee2e2', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#dc2626' }}>{logsStats.api_errors || 0}</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>Errors</div>
              </div>
            </div>

            {/* Filter Tabs */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {[
                { id: 'all', label: 'All Logs' },
                { id: 'oauth', label: '🔐 OAuth' },
                { id: 'webhooks', label: '📥 Webhooks' },
                { id: 'contacts', label: '👤 Contacts' },
                { id: 'audit', label: '📋 Audit' },
                { id: 'errors', label: '⚠️ Errors' }
              ].map(filter => (
                <Button
                  key={filter.id}
                  onClick={() => handleLogFilterChange(filter.id)}
                  className="btn"
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    background: logFilter === filter.id ? '#1e293b' : '#f1f5f9',
                    color: logFilter === filter.id ? 'white' : '#64748b',
                    border: 'none'
                  }}
                >
                  {filter.label}
                </Button>
              ))}
              <Button
                onClick={() => loadLogs(logFilter)}
                className="btn"
                style={{ padding: '6px 12px', fontSize: '12px', marginLeft: 'auto' }}
              >
                🔄 Refresh
              </Button>
            </div>

            {/* Logs Table */}
            {loadingLogs ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                Loading logs...
              </div>
            ) : logs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                No logs found for this filter.
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {logs.map((log, index) => (
                  <div 
                    key={index} 
                    style={{ 
                      padding: '12px',
                      borderBottom: '1px solid #e2e8f0',
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'flex-start'
                    }}
                  >
                    <div style={{ 
                      fontSize: '20px',
                      width: '32px',
                      textAlign: 'center'
                    }}>
                      {getLogIcon(log.log_type)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ 
                          fontSize: '12px', 
                          fontWeight: '600',
                          color: getLogColor(log.log_type),
                          background: `${getLogColor(log.log_type)}15`,
                          padding: '2px 8px',
                          borderRadius: '4px'
                        }}>
                          {log.log_category || log.event || log.action || log.event_type || 'Event'}
                        </span>
                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                          {formatLogTime(log)}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#374151' }}>
                        {log.log_type === 'oauth' && (
                          <>
                            <strong>Location:</strong> {log.ghl_location_id || 'N/A'} | 
                            <strong> Status:</strong> {log.status || 'pending'}
                          </>
                        )}
                        {log.log_type === 'webhook' && (
                          <>
                            <strong>Company:</strong> {log.company_name || 'N/A'} | 
                            <strong> Location:</strong> {log.location_id || 'N/A'} |
                            <strong> Status:</strong> {log.status || 'N/A'}
                          </>
                        )}
                        {log.log_type === 'contact' && (
                          <>
                            <strong>Event:</strong> {log.event} | 
                            <strong> Contact:</strong> {log.contact_id || 'N/A'}
                            {log.email && <> | <strong>Email:</strong> {log.email}</>}
                          </>
                        )}
                        {log.log_type === 'audit' && (
                          <>
                            <strong>Action:</strong> {log.action} | 
                            <strong> Admin:</strong> {log.admin || 'N/A'}
                            {log.target_user && <> | <strong>Target:</strong> {log.target_user}</>}
                            {log.code && <> | <strong>Code:</strong> {log.code}</>}
                          </>
                        )}
                        {log.log_type === 'error' && (
                          <>
                            <strong>Service:</strong> {log.service || 'N/A'} | 
                            <strong> Endpoint:</strong> {log.endpoint || 'N/A'}
                            {log.status_code && <> | <strong>Status:</strong> {log.status_code}</>}
                            {log.user && <> | <strong>User:</strong> {log.user}</>}
                            <br />
                            <span style={{ color: '#dc2626' }}>{log.error_message}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Discount Codes Modal */}
      <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
        <DialogContent style={{ maxWidth: "750px", maxHeight: "85vh", overflowY: "auto" }}>
          <DialogHeader>
            <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              💰 Discount Codes
            </DialogTitle>
          </DialogHeader>
          
          <div style={{ padding: "10px 0" }}>
            {/* Create Button */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                Create and manage promotional discount codes for subscriptions
              </p>
              <Button
                onClick={() => setShowCreateDiscount(!showCreateDiscount)}
                className="btn btn-primary"
                style={{ 
                  background: showCreateDiscount ? '#f1f5f9' : 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                  color: showCreateDiscount ? '#64748b' : 'white'
                }}
                data-testid="create-discount-btn"
              >
                {showCreateDiscount ? 'Cancel' : '+ Create Discount'}
              </Button>
            </div>

            {/* Create Discount Form */}
            {showCreateDiscount && (
              <div style={{ 
                background: '#f0fdf4', 
                border: '1px solid #bbf7d0',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                  {/* Code */}
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '4px' }}>
                      Code (optional)
                    </label>
                    <input
                      type="text"
                      value={newDiscount.code}
                      onChange={(e) => setNewDiscount({...newDiscount, code: e.target.value.toUpperCase()})}
                      placeholder="Auto-generate"
                      className="form-input"
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', fontSize: '13px' }}
                      data-testid="discount-code-input"
                    />
                  </div>

                  {/* Discount Type */}
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '4px' }}>
                      Type
                    </label>
                    <select
                      value={newDiscount.discount_type}
                      onChange={(e) => setNewDiscount({...newDiscount, discount_type: e.target.value})}
                      className="form-input"
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', fontSize: '13px' }}
                      data-testid="discount-type-select"
                    >
                      <option value="percent">Percentage Off</option>
                      <option value="fixed">Fixed Amount Off</option>
                    </select>
                  </div>

                  {/* Discount Value */}
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '4px' }}>
                      {newDiscount.discount_type === 'percent' ? 'Percentage (%)' : 'Amount ($)'}
                    </label>
                    <input
                      type="number"
                      value={newDiscount.discount_value}
                      onChange={(e) => setNewDiscount({...newDiscount, discount_value: parseFloat(e.target.value) || 0})}
                      min="1"
                      max={newDiscount.discount_type === 'percent' ? 100 : 1000}
                      className="form-input"
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', fontSize: '13px' }}
                      data-testid="discount-value-input"
                    />
                  </div>

                  {/* Duration */}
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '4px' }}>
                      Duration
                    </label>
                    <select
                      value={newDiscount.duration}
                      onChange={(e) => setNewDiscount({...newDiscount, duration: e.target.value})}
                      className="form-input"
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', fontSize: '13px' }}
                      data-testid="discount-duration-select"
                    >
                      <option value="once">One-time</option>
                      <option value="forever">Lifetime</option>
                      <option value="repeating">Limited Months</option>
                    </select>
                  </div>

                  {/* Duration Months (if repeating) */}
                  {newDiscount.duration === 'repeating' && (
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Months
                      </label>
                      <input
                        type="number"
                        value={newDiscount.duration_months}
                        onChange={(e) => setNewDiscount({...newDiscount, duration_months: parseInt(e.target.value) || 3})}
                        min="1"
                        max="24"
                        className="form-input"
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', fontSize: '13px' }}
                        data-testid="discount-months-input"
                      />
                    </div>
                  )}

                  {/* Max Redemptions */}
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '4px' }}>
                      Max Uses
                    </label>
                    <input
                      type="number"
                      value={newDiscount.max_redemptions || ''}
                      onChange={(e) => setNewDiscount({...newDiscount, max_redemptions: e.target.value ? parseInt(e.target.value) : null})}
                      placeholder="Unlimited"
                      min="1"
                      className="form-input"
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', fontSize: '13px' }}
                      data-testid="discount-max-uses-input"
                    />
                  </div>

                  {/* Description */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '4px' }}>
                      Description
                    </label>
                    <input
                      type="text"
                      value={newDiscount.description}
                      onChange={(e) => setNewDiscount({...newDiscount, description: e.target.value})}
                      placeholder="e.g., Summer promotion"
                      className="form-input"
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', fontSize: '13px' }}
                      data-testid="discount-description-input"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleCreateDiscount}
                  disabled={processing}
                  className="btn btn-primary"
                  style={{ 
                    marginTop: '12px',
                    width: '100%',
                    background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' 
                  }}
                  data-testid="submit-discount-btn"
                >
                  {processing ? 'Creating...' : 'Create Discount Code'}
                </Button>
              </div>
            )}

            {/* Discount Codes Table */}
            {loadingDiscounts ? (
              <div style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                Loading discount codes...
              </div>
            ) : discountCodes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                No discount codes yet. Create one to get started!
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                      <th style={{ padding: '10px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Code</th>
                      <th style={{ padding: '10px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Discount</th>
                      <th style={{ padding: '10px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Duration</th>
                      <th style={{ padding: '10px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Uses</th>
                      <th style={{ padding: '10px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Status</th>
                      <th style={{ padding: '10px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {discountCodes.map((discount) => (
                      <tr key={discount.code} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '10px' }}>
                          <code style={{ 
                            background: '#f1f5f9', 
                            padding: '3px 6px', 
                            borderRadius: '4px',
                            fontWeight: '600',
                            fontSize: '12px'
                          }}>
                            {discount.code}
                          </code>
                          {discount.stripe_synced && (
                            <span style={{ marginLeft: '6px', fontSize: '10px', color: '#16a34a' }}>✓ Stripe</span>
                          )}
                        </td>
                        <td style={{ padding: '10px', fontSize: '12px', color: '#16a34a', fontWeight: '600' }}>
                          {discount.discount_type === 'percent' ? `${discount.discount_value}%` : `$${discount.discount_value}`}
                        </td>
                        <td style={{ padding: '10px', fontSize: '12px', color: '#64748b' }}>
                          {discount.duration === 'once' && 'One-time'}
                          {discount.duration === 'forever' && '♾️ Forever'}
                          {discount.duration === 'repeating' && `${discount.duration_months}mo`}
                        </td>
                        <td style={{ padding: '10px', fontSize: '12px' }}>
                          {discount.redemptions_count || 0}{discount.max_redemptions ? `/${discount.max_redemptions}` : ''}
                        </td>
                        <td style={{ padding: '10px' }}>
                          <span style={{ 
                            background: discount.is_active ? '#d1fae5' : '#fee2e2',
                            color: discount.is_active ? '#047857' : '#dc2626',
                            padding: '2px 6px',
                            borderRadius: '10px',
                            fontSize: '10px',
                            fontWeight: '600'
                          }}>
                            {discount.is_active ? 'ACTIVE' : 'OFF'}
                          </span>
                        </td>
                        <td style={{ padding: '10px' }}>
                          {discount.is_active && (
                            <Button
                              onClick={() => handleDeleteDiscount(discount.code)}
                              className="btn"
                              style={{ 
                                padding: '3px 6px', 
                                fontSize: '10px',
                                background: '#fee2e2',
                                color: '#dc2626',
                                border: 'none'
                              }}
                              data-testid={`delete-discount-${discount.code}-btn`}
                            >
                              Deactivate
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <div className="content-card">
        <div className="card-header">
          <h2 className="card-title">User Management ({users.length} users)</h2>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e2e8f0", textAlign: "left" }}>
                <th style={{ padding: "12px", fontSize: "13px", fontWeight: "600", color: "#475569" }}>Username</th>
                <th style={{ padding: "12px", fontSize: "13px", fontWeight: "600", color: "#475569" }}>Email</th>
                <th style={{ padding: "12px", fontSize: "13px", fontWeight: "600", color: "#475569" }}>Full Name</th>
                <th style={{ padding: "12px", fontSize: "13px", fontWeight: "600", color: "#475569" }}>Role</th>
                <th style={{ padding: "12px", fontSize: "13px", fontWeight: "600", color: "#475569" }}>Status</th>
                <th style={{ padding: "12px", fontSize: "13px", fontWeight: "600", color: "#475569" }}>Last Login</th>
                <th style={{ padding: "12px", fontSize: "13px", fontWeight: "600", color: "#475569" }}>Scans</th>
                <th style={{ padding: "12px", fontSize: "13px", fontWeight: "600", color: "#475569" }}>Imports</th>
                <th style={{ padding: "12px", fontSize: "13px", fontWeight: "600", color: "#475569" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, idx) => {
                const isDeleted = !!user.deleted_at;
                const deletedDaysAgo = isDeleted ? Math.floor((new Date() - new Date(user.deleted_at)) / (1000 * 60 * 60 * 24)) : 0;
                const canRecover = isDeleted && deletedDaysAgo <= 31;
                
                return (
                <tr key={idx} style={{ 
                  borderBottom: "1px solid #e2e8f0",
                  background: isDeleted ? "#fef2f2" : "transparent",
                  opacity: isDeleted ? 0.8 : 1
                }}>
                  <td style={{ padding: "12px", fontSize: "14px", fontWeight: "600" }}>
                    {user.username}
                    {isDeleted && (
                      <span style={{ 
                        display: "block", 
                        fontSize: "11px", 
                        color: "#dc2626",
                        fontWeight: "normal"
                      }}>
                        Deleted {deletedDaysAgo} day{deletedDaysAgo !== 1 ? 's' : ''} ago
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "12px", fontSize: "14px" }}>{user.email || "-"}</td>
                  <td style={{ padding: "12px", fontSize: "14px" }}>{user.full_name || "-"}</td>
                  <td style={{ padding: "12px" }}>
                    <select
                      value={user.role || "user"}
                      onChange={(e) => handleSetRole(user.username, e.target.value)}
                      disabled={isDeleted}
                      style={{
                        padding: "4px 8px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: user.role === "admin" ? "#dc2626" : "#0369a1",
                        cursor: isDeleted ? "not-allowed" : "pointer",
                        opacity: isDeleted ? 0.5 : 1
                      }}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td style={{ padding: "12px" }}>
                    {isDeleted ? (
                      <span style={{
                        padding: "4px 8px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "600",
                        background: "#fecaca",
                        color: "#991b1b"
                      }}>
                        Deleted
                      </span>
                    ) : (
                      <span style={{
                        padding: "4px 8px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "600",
                        background: user.disabled ? "#fee2e2" : "#dcfce7",
                        color: user.disabled ? "#dc2626" : "#16a34a"
                      }}>
                        {user.disabled ? "Disabled" : "Active"}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "12px", fontSize: "14px" }}>
                    {user.last_login ? new Date(user.last_login).toLocaleString() : "Never"}
                  </td>
                  <td style={{ padding: "12px", fontSize: "14px", textAlign: "center" }}>{user.scan_count || 0}</td>
                  <td style={{ padding: "12px", fontSize: "14px", textAlign: "center" }}>{user.import_count || 0}</td>
                  <td style={{ padding: "12px" }}>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      {isDeleted ? (
                        <>
                          {canRecover && (
                            <button
                              onClick={() => handleRecoverUser(user.username)}
                              className="btn"
                              style={{ 
                                fontSize: "12px", 
                                padding: "4px 8px", 
                                background: "#dcfce7", 
                                color: "#16a34a",
                                border: "1px solid #bbf7d0"
                              }}
                              data-testid={`recover-${user.username}-btn`}
                            >
                              ♻️ Recover
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteUser(user.username)}
                            className="btn"
                            style={{ fontSize: "12px", padding: "4px 8px", background: "#fee2e2", color: "#dc2626" }}
                          >
                            🗑️ Permanent Delete
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowResetModal(true);
                            }}
                            className="btn btn-secondary"
                            style={{ fontSize: "12px", padding: "4px 8px" }}
                          >
                            🔑 Reset PW
                          </button>
                          <button
                            onClick={() => handleToggleStatus(user.username, user.disabled)}
                            className="btn btn-secondary"
                            style={{ fontSize: "12px", padding: "4px 8px" }}
                          >
                            {user.disabled ? "✅ Enable" : "🚫 Disable"}
                          </button>
                          <button
                            onClick={() => handleViewActivity(user)}
                            className="btn btn-secondary"
                            style={{ fontSize: "12px", padding: "4px 8px" }}
                          >
                            📊 Activity
                          </button>
                          <button
                            onClick={() => handleViewSubscription(user)}
                            className="btn btn-secondary"
                            style={{ fontSize: "12px", padding: "4px 8px" }}
                            data-testid={`subscription-${user.username}-btn`}
                          >
                            💳 Subscription
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.username)}
                            className="btn"
                            style={{ fontSize: "12px", padding: "4px 8px", background: "#fee2e2", color: "#dc2626" }}
                          >
                            🗑️ Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reset Password Modal */}
      <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password for {selectedUser?.username}</DialogTitle>
          </DialogHeader>
          <div style={{ padding: "20px 0" }}>
            <p style={{ marginBottom: "16px", fontSize: "14px", color: "#64748b" }}>
              Set a temporary password. The user will be required to change it on their next login.
            </p>
            <div className="form-group">
              <label className="form-label">New Temporary Password</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoFocus
              />
            </div>
            <div style={{ display: "flex", gap: "12px", marginTop: "20px" }}>
              <Button
                onClick={handleResetPassword}
                disabled={processing}
                className="btn btn-primary"
              >
                {processing ? "Resetting..." : "Reset Password"}
              </Button>
              <Button
                onClick={() => {
                  setShowResetModal(false);
                  setNewPassword("");
                  setSelectedUser(null);
                }}
                className="btn btn-secondary"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Activity Modal */}
      <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Activity for {selectedUser?.username}</DialogTitle>
          </DialogHeader>
          <div style={{ padding: "20px 0", maxHeight: "500px", overflowY: "auto" }}>
            {activity ? (
              <>
                <div style={{ marginBottom: "24px" }}>
                  <h4 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px" }}>Recent Scans ({activity.recent_scans?.length || 0})</h4>
                  {activity.recent_scans && activity.recent_scans.length > 0 ? (
                    <div style={{ fontSize: "13px" }}>
                      {activity.recent_scans.map((scan, idx) => (
                        <div key={idx} style={{ padding: "8px", background: "#f8fafc", borderRadius: "6px", marginBottom: "6px" }}>
                          <div><strong>ID:</strong> {scan.scan_id}</div>
                          <div><strong>Started:</strong> {new Date(scan.started_at).toLocaleString()}</div>
                          <div><strong>Status:</strong> {scan.status}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: "13px", color: "#64748b" }}>No scans yet</p>
                  )}
                </div>

                <div>
                  <h4 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px" }}>Recent Imports ({activity.recent_imports?.length || 0})</h4>
                  {activity.recent_imports && activity.recent_imports.length > 0 ? (
                    <div style={{ fontSize: "13px" }}>
                      {activity.recent_imports.map((imp, idx) => (
                        <div key={idx} style={{ padding: "8px", background: "#f8fafc", borderRadius: "6px", marginBottom: "6px" }}>
                          <div><strong>Name:</strong> {imp.name || "-"}</div>
                          <div><strong>Email:</strong> {imp.email || "-"}</div>
                          <div><strong>Date:</strong> {new Date(imp.import_date).toLocaleString()}</div>
                          <div><strong>Status:</strong> {imp.status}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: "13px", color: "#64748b" }}>No imports yet</p>
                  )}
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <div className="spinner" style={{ margin: "0 auto" }}></div>
                <p style={{ marginTop: "12px", color: "#64748b" }}>Loading activity...</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription Management Modal */}
      <Dialog open={showSubscriptionModal} onOpenChange={setShowSubscriptionModal}>
        <DialogContent style={{ maxWidth: "550px", maxHeight: "85vh", overflowY: "auto" }}>
          <DialogHeader>
            <DialogTitle>Subscription for {selectedUser?.username}</DialogTitle>
          </DialogHeader>
          <div style={{ padding: "20px 0" }}>
            {subscription ? (
              <>
                {/* Current Plan Info */}
                <div style={{ 
                  background: "#f8fafc", 
                  borderRadius: "12px", 
                  padding: "16px",
                  marginBottom: "20px"
                }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                    <div>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>Plan</span>
                      <p style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b", margin: "4px 0 0" }}>
                        {subscription.subscription?.plan?.charAt(0).toUpperCase() + subscription.subscription?.plan?.slice(1) || "Free"}
                        {subscription.subscription?.forced_by_admin && (
                          <span style={{ 
                            fontSize: "10px", 
                            background: "#fef3c7", 
                            color: "#92400e",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            marginLeft: "8px"
                          }}>
                            FORCED
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>Status</span>
                      <p style={{ fontSize: "16px", fontWeight: "700", color: "#16a34a", margin: "4px 0 0" }}>
                        {subscription.subscription?.status || "Active"}
                      </p>
                    </div>
                    <div>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>Leads Used</span>
                      <p style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b", margin: "4px 0 0" }}>
                        {subscription.limit_info?.leads_used || 0} / {subscription.limit_info?.leads_limit ?? 10}
                      </p>
                    </div>
                    <div>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>Payment Status</span>
                      <p style={{ 
                        fontSize: "14px", 
                        fontWeight: "600", 
                        color: subscription.subscription?.has_card_on_file ? "#16a34a" : "#dc2626", 
                        margin: "4px 0 0" 
                      }}>
                        {subscription.subscription?.has_card_on_file ? "💳 Card on File" : "⚠️ No Card"}
                      </p>
                    </div>
                  </div>
                  
                  {/* Forced subscription info */}
                  {subscription.subscription?.forced_by_admin && (
                    <div style={{ 
                      marginTop: "12px", 
                      padding: "10px", 
                      background: "#fef3c7", 
                      borderRadius: "8px",
                      fontSize: "12px"
                    }}>
                      <p style={{ margin: 0, color: "#92400e" }}>
                        <strong>Forced by:</strong> {subscription.subscription?.forced_by || "Admin"} on {formatDate(subscription.subscription?.forced_at)}
                      </p>
                      <p style={{ margin: "4px 0 0", color: "#92400e" }}>
                        <strong>Expires:</strong> {subscription.subscription?.forced_is_indefinite ? "Never (Indefinite)" : formatDate(subscription.subscription?.forced_expires_at)}
                      </p>
                      {subscription.subscription?.forced_reason && (
                        <p style={{ margin: "4px 0 0", color: "#92400e" }}>
                          <strong>Reason:</strong> {subscription.subscription?.forced_reason}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Force Subscription Section */}
                <div style={{ 
                  background: "#fef2f2", 
                  borderRadius: "12px", 
                  padding: "16px",
                  marginBottom: "20px",
                  border: "1px solid #fecaca"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showForceSubscription ? "16px" : 0 }}>
                    <div>
                      <h4 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "4px", color: "#991b1b" }}>
                        Force Change Subscription
                      </h4>
                      <p style={{ fontSize: "12px", color: "#b91c1c", margin: 0 }}>
                        Override user's subscription without payment
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowForceSubscription(!showForceSubscription)}
                      className="btn"
                      style={{
                        background: showForceSubscription ? "#fee2e2" : "#dc2626",
                        color: showForceSubscription ? "#dc2626" : "white",
                        border: "none",
                        fontSize: "12px",
                        padding: "8px 12px"
                      }}
                      data-testid="toggle-force-subscription-btn"
                    >
                      {showForceSubscription ? "Cancel" : "Force Change"}
                    </Button>
                  </div>

                  {showForceSubscription && (
                    <div style={{ borderTop: "1px solid #fecaca", paddingTop: "16px" }}>
                      {/* Plan Selection */}
                      <div style={{ marginBottom: "12px" }}>
                        <label style={{ fontSize: "12px", fontWeight: "600", color: "#334155", display: "block", marginBottom: "6px" }}>
                          Select Plan
                        </label>
                        <select
                          value={forcePlan}
                          onChange={(e) => setForcePlan(e.target.value)}
                          className="form-input"
                          style={{ width: "100%", padding: "10px", borderRadius: "8px" }}
                          data-testid="force-plan-select"
                        >
                          <option value="">-- Select a plan --</option>
                          {subscriptionPlans.map(plan => (
                            <option key={plan.id} value={plan.id}>
                              {plan.name} - {plan.price}/mo ({plan.leads} leads)
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Indefinite Toggle */}
                      <div style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "10px", 
                        marginBottom: "12px",
                        padding: "10px",
                        background: forceIndefinite ? "#dcfce7" : "#f1f5f9",
                        borderRadius: "8px"
                      }}>
                        <input
                          type="checkbox"
                          checked={forceIndefinite}
                          onChange={(e) => setForceIndefinite(e.target.checked)}
                          id="indefinite-checkbox"
                          style={{ width: "18px", height: "18px", accentColor: "#16a34a" }}
                          data-testid="force-indefinite-checkbox"
                        />
                        <div>
                          <label htmlFor="indefinite-checkbox" style={{ fontSize: "13px", fontWeight: "600", color: "#334155", cursor: "pointer" }}>
                            Indefinite Subscription
                          </label>
                          <p style={{ fontSize: "11px", color: "#64748b", margin: 0 }}>
                            {forceIndefinite 
                              ? "Subscription will never expire until manually changed" 
                              : "Subscription expires in 30 days (requires card on file for renewal)"
                            }
                          </p>
                        </div>
                      </div>

                      {/* Warning for no card */}
                      {!subscription.subscription?.has_card_on_file && !forceIndefinite && forcePlan && forcePlan !== "free" && (
                        <div style={{ 
                          background: "#fef3c7", 
                          padding: "10px", 
                          borderRadius: "8px", 
                          marginBottom: "12px",
                          fontSize: "12px",
                          color: "#92400e"
                        }}>
                          ⚠️ <strong>Warning:</strong> User has no card on file. This subscription will expire in 30 days unless they add a payment method.
                        </div>
                      )}

                      {/* Reason Input */}
                      <div style={{ marginBottom: "12px" }}>
                        <label style={{ fontSize: "12px", fontWeight: "600", color: "#334155", display: "block", marginBottom: "6px" }}>
                          Reason (Optional)
                        </label>
                        <input
                          type="text"
                          value={forceReason}
                          onChange={(e) => setForceReason(e.target.value)}
                          placeholder="e.g., Customer support, promotional offer..."
                          className="form-input"
                          style={{ width: "100%", padding: "10px", borderRadius: "8px" }}
                          data-testid="force-reason-input"
                        />
                      </div>

                      {/* Apply Button */}
                      <Button
                        onClick={handleForceSubscription}
                        disabled={processing || !forcePlan}
                        className="btn btn-primary"
                        style={{
                          width: "100%",
                          background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                          border: "none",
                          padding: "12px"
                        }}
                        data-testid="apply-force-subscription-btn"
                      >
                        {processing ? "Processing..." : `Force Change to ${subscriptionPlans.find(p => p.id === forcePlan)?.name || "Selected Plan"}`}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Admin Lead Override */}
                <div style={{ marginBottom: "20px" }}>
                  <h4 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#334155" }}>
                    Lead Limit Override
                  </h4>
                  <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "12px" }}>
                    Set a custom lead limit for this user, overriding their plan's default limit.
                  </p>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      type="number"
                      value={leadOverride}
                      onChange={(e) => setLeadOverride(e.target.value)}
                      placeholder="e.g., 500"
                      className="form-input"
                      style={{ flex: 1, maxWidth: "150px" }}
                      data-testid="lead-override-input"
                    />
                    <Button
                      onClick={handleSetLeadOverride}
                      disabled={processing}
                      className="btn btn-primary"
                      data-testid="set-lead-override-btn"
                    >
                      {processing ? "..." : "Set"}
                    </Button>
                    {subscription.subscription?.admin_lead_override !== null && subscription.subscription?.admin_lead_override !== undefined && (
                      <Button
                        onClick={() => {
                          setLeadOverride("");
                          handleSetLeadOverride();
                        }}
                        disabled={processing}
                        className="btn btn-secondary"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  {subscription.subscription?.admin_lead_override !== null && subscription.subscription?.admin_lead_override !== undefined && (
                    <p style={{ fontSize: "12px", color: "#f59e0b", marginTop: "8px" }}>
                      Current override: {subscription.subscription.admin_lead_override} leads
                    </p>
                  )}
                </div>

                {/* Apply Discount Code Section */}
                <div style={{ 
                  background: "#faf5ff", 
                  borderRadius: "12px", 
                  padding: "16px",
                  border: "1px solid #e9d5ff",
                  marginBottom: "20px"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showApplyDiscountModal ? "16px" : 0 }}>
                    <div>
                      <h4 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "4px", color: "#7c3aed" }}>
                        Apply Discount Code
                      </h4>
                      <p style={{ fontSize: "13px", color: "#64748b", margin: 0 }}>
                        Apply a promotional discount to this user's account
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowApplyDiscountModal(!showApplyDiscountModal)}
                      className="btn"
                      style={{
                        background: showApplyDiscountModal ? "#f3e8ff" : "#7c3aed",
                        color: showApplyDiscountModal ? "#7c3aed" : "white",
                        border: "none"
                      }}
                      data-testid="toggle-apply-discount-btn"
                    >
                      {showApplyDiscountModal ? "Cancel" : "Apply Discount"}
                    </Button>
                  </div>

                  {showApplyDiscountModal && (
                    <div style={{ borderTop: "1px solid #e9d5ff", paddingTop: "16px" }}>
                      <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                        <select
                          value={applyDiscountCode}
                          onChange={(e) => setApplyDiscountCode(e.target.value)}
                          className="form-input"
                          style={{ flex: 1, padding: "10px", borderRadius: "8px" }}
                          data-testid="apply-discount-select"
                        >
                          <option value="">-- Select discount code --</option>
                          {discountCodes.filter(d => d.is_active).map(d => (
                            <option key={d.code} value={d.code}>
                              {d.code} - {d.discount_type === 'percent' ? `${d.discount_value}%` : `$${d.discount_value}`} ({d.duration === 'forever' ? 'Lifetime' : d.duration === 'once' ? 'One-time' : `${d.duration_months}mo`})
                            </option>
                          ))}
                        </select>
                        <Button
                          onClick={handleApplyDiscountToUser}
                          disabled={processing || !applyDiscountCode}
                          className="btn btn-primary"
                          style={{ background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)" }}
                          data-testid="confirm-apply-discount-btn"
                        >
                          {processing ? "..." : "Apply"}
                        </Button>
                      </div>
                      <p style={{ fontSize: "11px", color: "#64748b", margin: 0 }}>
                        {applyDiscountCode ? 
                          "Discount will be applied to their existing subscription (if any) or next purchase." :
                          "Select a discount code to apply to this user."
                        }
                      </p>
                    </div>
                  )}
                </div>

                {/* Pic-to-Lead unlimited toggle */}
                <div style={{ 
                  background: subscription.subscription?.popl_enabled ? "#f0fdf4" : "#fef2f2", 
                  borderRadius: "12px", 
                  padding: "16px",
                  border: subscription.subscription?.popl_enabled ? "1px solid #bbf7d0" : "1px solid #fecaca"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <h4 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "4px", color: "#334155" }}>
                        Unlimited Pic-to-Lead
                      </h4>
                      <p style={{ fontSize: "13px", color: "#64748b", margin: 0 }}>
                        {subscription.subscription?.popl_enabled 
                          ? "Unlimited card scans (free plan normally gets 5/month)" 
                          : "Give this free user unlimited card scans"}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleTogglePopl(!subscription.subscription?.popl_enabled)}
                      disabled={processing}
                      className="btn"
                      style={{
                        background: subscription.subscription?.popl_enabled ? "#fee2e2" : "#dcfce7",
                        color: subscription.subscription?.popl_enabled ? "#dc2626" : "#16a34a",
                        border: "none"
                      }}
                      data-testid="toggle-popl-btn"
                    >
                      {subscription.subscription?.popl_enabled ? "Disable" : "Enable"}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <div className="spinner" style={{ margin: "0 auto" }}></div>
                <p style={{ marginTop: "12px", color: "#64748b" }}>Loading subscription...</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminDashboard;
